"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { Plus, Trash2, Upload, FileCheck } from "lucide-react";
import { uploadDocumentAction, submitOnboardingAction } from "@/app/actions";
import type { RequirementsResponse } from "@/lib/conduit";

type Person = {
  firstName: string;
  lastName: string;
  email: string;
  birthDate: string;
  nationality: string;
  governmentIdType: string;
  governmentIdNumber: string;
  governmentIdCountry: string;
  roles: string[];
  ownershipPercent: string;
};

type Doc = { id: string; fileName: string };

const ROLES = ["LEGAL_REPRESENTATIVE", "CONTROLLING_PERSON", "BENEFICIAL_OWNER"];
// The API expects a TAX identification type here — SSN (US) or NATIONAL_ID (non-US).
// Identity documents (passport, INE, driver's license) are uploaded as documents instead.
const ID_TYPES = ["NATIONAL_ID", "SSN"];
const STEPS = ["Business", "People", "Documents", "Review"];

const emptyPerson: Person = {
  firstName: "",
  lastName: "",
  email: "",
  birthDate: "",
  nationality: "MEX",
  governmentIdType: "NATIONAL_ID",
  governmentIdNumber: "",
  governmentIdCountry: "MEX",
  roles: ["LEGAL_REPRESENTATIVE"],
  ownershipPercent: "",
};

export default function OnboardingWizard({ requirements }: { requirements: RequirementsResponse | null }) {
  const router = useRouter();
  const [step, setStep] = useState(0);
  const [pending, start] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Array<{ pointer?: string; detail?: string }>>([]);

  const [biz, setBiz] = useState({
    legalName: "",
    taxId: "",
    businessEntityId: "",
    website: "",
    companyEmail: "",
    companyPhone: "",
    legalStructure: "Sociedad Anónima de Capital Variable",
    coreIndustry: "",
    clientReferenceId: "",
    country: "MX",
    addressLine1: "",
    city: "",
    state: "",
    postalCode: "",
  });
  const [persons, setPersons] = useState<Person[]>([{ ...emptyPerson }]);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [certified, setCertified] = useState(false);

  function set<K extends keyof typeof biz>(k: K, v: string) {
    setBiz((b) => ({ ...b, [k]: v }));
  }

  function setPerson(i: number, patch: Partial<Person>) {
    setPersons((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  }

  async function onUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setError(null);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.set("file", file);
      fd.set("purpose", "organization_onboarding");
      fd.set("name", file.name);
      const res = await uploadDocumentAction(fd);
      if (res.ok) {
        setDocs((d) => [...d, { id: res.data.id, fileName: res.data.fileName ?? file.name }]);
      } else {
        setError(`${file.name}: ${res.error}`);
      }
    }
  }

  function buildPayload() {
    return {
      clientReferenceId: biz.clientReferenceId || biz.legalName.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-"),
      documentIds: docs.map((d) => d.id),
      businessInfo: {
        legalName: biz.legalName,
        taxId: biz.taxId,
        ...(biz.businessEntityId ? { businessEntityId: biz.businessEntityId } : {}),
        ...(biz.website ? { website: biz.website } : {}),
        companyEmail: biz.companyEmail,
        ...(biz.companyPhone ? { companyPhone: biz.companyPhone } : {}),
      },
      registeredAddress: {
        country: biz.country,
        addressLine1: biz.addressLine1,
        city: biz.city,
        state: biz.state,
        postalCode: biz.postalCode,
      },
      companyClassification: {
        legalStructure: biz.legalStructure,
        ...(biz.coreIndustry ? { coreIndustry: biz.coreIndustry } : {}),
      },
      ownership: {
        persons: persons.map((p) => ({
          firstName: p.firstName,
          lastName: p.lastName,
          ...(p.email ? { email: p.email } : {}),
          birthDate: p.birthDate,
          nationality: p.nationality,
          ...(p.governmentIdNumber
            ? {
                governmentIdType: p.governmentIdType,
                governmentIdNumber: p.governmentIdNumber,
                governmentIdCountry: p.governmentIdCountry,
              }
            : {}),
          roles: p.roles,
          ...(p.ownershipPercent ? { ownershipPercent: Number(p.ownershipPercent) } : {}),
        })),
      },
      certification: { termsAndConditions: certified, consentToElectronicSignatures: certified },
    };
  }

  function submit() {
    setError(null);
    setFieldErrors([]);
    start(async () => {
      const res = await submitOnboardingAction(buildPayload());
      if (res.ok) {
        router.push(`/onboarding/${res.data.id}`);
      } else {
        setError(res.error);
        setFieldErrors(res.fieldErrors ?? []);
      }
    });
  }

  const input = (label: string, k: keyof typeof biz, placeholder = "", type = "text") => (
    <div>
      <label className="label">{label}</label>
      <input
        className="field"
        type={type}
        value={biz[k]}
        placeholder={placeholder}
        onChange={(e) => set(k, e.target.value)}
      />
    </div>
  );

  return (
    <div className="max-w-3xl">
      {/* Stepper */}
      <div className="mb-8 flex items-center gap-2">
        {STEPS.map((s, i) => (
          <button
            key={s}
            onClick={() => setStep(i)}
            className="pill"
            style={
              i === step
                ? { background: "var(--c-green)", color: "var(--c-charcoal)" }
                : i < step
                  ? { background: "var(--c-charcoal)", color: "var(--c-white)" }
                  : { boxShadow: "inset 0 0 0 1px var(--c-hairline)", color: "var(--c-ink-soft)" }
            }
          >
            {i + 1} · {s.toLowerCase()}
          </button>
        ))}
      </div>

      {step === 0 && (
        <section className="card rise p-8">
          <h2 className="display mb-6 text-[26px]">The business</h2>
          <div className="grid grid-cols-2 gap-4">
            {input("Legal name", "legalName", "Tecnologias Conduit Mexico")}
            {input("Tax ID (RFC)", "taxId", "TCM250710FF2")}
            {input("Registry ID / Folio Mercantil", "businessEntityId", "optional")}
            {input("Legal structure", "legalStructure")}
            {input("Company email", "companyEmail", "finance@company.com", "email")}
            {input("Company phone", "companyPhone", "+52...", "tel")}
            {input("Website", "website", "https://")}
            {input("Core industry", "coreIndustry", "optional")}
            {input("Client reference", "clientReferenceId", "your internal id (optional)")}
          </div>
          <h3 className="display mt-8 mb-4 text-[20px]">Registered address</h3>
          <div className="grid grid-cols-2 gap-4">
            {input("Address line 1", "addressLine1")}
            {input("City", "city")}
            {input("State", "state", "e.g. MX-CMX")}
            {input("Postal code", "postalCode")}
            {input("Country", "country", "MX")}
          </div>
        </section>
      )}

      {step === 1 && (
        <section className="rise flex flex-col gap-4">
          {persons.map((p, i) => (
            <div key={i} className="card p-8">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="display text-[22px]">Person {i + 1}</h2>
                {persons.length > 1 && (
                  <button
                    className="btn btn-ghost"
                    onClick={() => setPersons((ps) => ps.filter((_, j) => j !== i))}
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">First name(s)</label>
                  <input className="field" value={p.firstName} onChange={(e) => setPerson(i, { firstName: e.target.value })} />
                </div>
                <div>
                  <label className="label">Last name(s)</label>
                  <input className="field" value={p.lastName} onChange={(e) => setPerson(i, { lastName: e.target.value })} />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input className="field" type="email" value={p.email} onChange={(e) => setPerson(i, { email: e.target.value })} />
                </div>
                <div>
                  <label className="label">Date of birth</label>
                  <input className="field" type="date" value={p.birthDate} onChange={(e) => setPerson(i, { birthDate: e.target.value })} />
                </div>
                <div>
                  <label className="label">Nationality (ISO3)</label>
                  <input className="field" value={p.nationality} onChange={(e) => setPerson(i, { nationality: e.target.value })} />
                </div>
                <div>
                  <label className="label">Ownership %</label>
                  <input className="field" type="number" min="0" max="100" value={p.ownershipPercent} placeholder="0" onChange={(e) => setPerson(i, { ownershipPercent: e.target.value })} />
                </div>
                <div>
                  <label className="label">Government ID type</label>
                  <select className="field" value={p.governmentIdType} onChange={(e) => setPerson(i, { governmentIdType: e.target.value })}>
                    {ID_TYPES.map((t) => (
                      <option key={t} value={t}>{t.replaceAll("_", " ").toLowerCase()}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Government ID number</label>
                  <input className="field" value={p.governmentIdNumber} onChange={(e) => setPerson(i, { governmentIdNumber: e.target.value })} />
                </div>
                <div>
                  <label className="label">ID country (ISO3)</label>
                  <input className="field" value={p.governmentIdCountry} onChange={(e) => setPerson(i, { governmentIdCountry: e.target.value })} />
                </div>
              </div>
              <div className="mt-4">
                <label className="label">Roles</label>
                <div className="flex gap-2">
                  {ROLES.map((r) => {
                    const on = p.roles.includes(r);
                    return (
                      <button
                        key={r}
                        className="pill"
                        style={
                          on
                            ? { background: "var(--c-charcoal)", color: "var(--c-white)" }
                            : { boxShadow: "inset 0 0 0 1px var(--c-hairline)", color: "var(--c-ink-soft)" }
                        }
                        onClick={() =>
                          setPerson(i, { roles: on ? p.roles.filter((x) => x !== r) : [...p.roles, r] })
                        }
                      >
                        {r.replaceAll("_", " ").toLowerCase()}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
          <button className="btn btn-ghost self-start" onClick={() => setPersons((ps) => [...ps, { ...emptyPerson }])}>
            <Plus size={14} /> Add person
          </button>
        </section>
      )}

      {step === 2 && (
        <section className="card rise p-8">
          <h2 className="display mb-2 text-[26px]">Documents</h2>
          {requirements?.documents && requirements.documents.length > 0 && (
            <div className="card-cream mb-6 p-4 text-[14px] font-light">
              <div className="eyebrow mb-2" style={{ color: "var(--c-ink-soft)" }}>
                required for {requirements.country ?? "this country"}
              </div>
              <ul className="list-inside list-disc">
                {requirements.documents.map((d, i) => (
                  <li key={i}>
                    {d.title ?? d.canonicalType}
                    {d.from ? ` — ${d.from}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <label
            className="flex cursor-pointer flex-col items-center gap-2 rounded-[16px] border border-dashed p-10 text-center"
            style={{ borderColor: "var(--c-hairline)" }}
          >
            <Upload size={22} />
            <span className="text-[15px] font-light">Drop files or click to upload (PDF, JPG, PNG — max 10 MB)</span>
            <input type="file" multiple hidden accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => onUpload(e.target.files)} />
          </label>
          {docs.length > 0 && (
            <ul className="mt-5 flex flex-col gap-2">
              {docs.map((d) => (
                <li key={d.id} className="flex items-center gap-3 text-[14px] font-light">
                  <FileCheck size={16} style={{ color: "var(--c-charcoal)" }} />
                  {d.fileName}
                  <span className="mono text-[12px]" style={{ color: "var(--c-ink-soft)" }}>
                    {d.id}
                  </span>
                  <button
                    className="ml-auto text-[12px] underline"
                    onClick={() => setDocs((x) => x.filter((y) => y.id !== d.id))}
                  >
                    remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {step === 3 && (
        <section className="card rise p-8">
          <h2 className="display mb-6 text-[26px]">Review & submit</h2>
          <div className="mb-6 grid grid-cols-2 gap-x-8 gap-y-3 text-[14px] font-light">
            <div><span className="label">Business</span>{biz.legalName || "—"} · {biz.taxId || "no tax id"}</div>
            <div><span className="label">Address</span>{biz.addressLine1 || "—"}, {biz.city} {biz.postalCode}, {biz.country}</div>
            <div><span className="label">People</span>{persons.map((p) => `${p.firstName} ${p.lastName}`.trim() || "—").join(" · ")}</div>
            <div><span className="label">Documents</span>{docs.length} uploaded</div>
          </div>
          <label className="flex items-start gap-3 text-[14px] font-light">
            <input type="checkbox" checked={certified} onChange={(e) => setCertified(e.target.checked)} className="mt-1" />
            <span>
              We certify the information is accurate, accept the terms and conditions, and consent to electronic
              signatures.
            </span>
          </label>
          <button className="btn btn-primary mt-6" disabled={pending || !certified} onClick={submit}>
            {pending ? "Submitting…" : "Submit application"}
          </button>
        </section>
      )}

      {error && (
        <div className="card-cream mt-5 p-4 text-[14px] font-light" style={{ color: "var(--c-danger)" }}>
          {error}
          {fieldErrors.length > 0 && (
            <ul className="mt-2 list-inside list-disc" style={{ color: "var(--c-charcoal)" }}>
              {fieldErrors.map((f, i) => (
                <li key={i}>
                  <span className="mono text-[12px]">{f.pointer}</span> {f.detail}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-8 flex justify-between">
        <button className="btn btn-ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
          Back
        </button>
        {step < STEPS.length - 1 && (
          <button className="btn btn-dark" onClick={() => setStep((s) => s + 1)}>
            Continue
          </button>
        )}
      </div>
    </div>
  );
}
